/**
 * HostBar - the persistent action bar for session-wide host controls (Bring
 * here, Guide everyone, Breakouts, Poll, Q&A, Lobby, Lock room).
 *
 * React replacement for src/ui/hud/hostBar.js plus multiplayerUI's six
 * paint* helpers: the action list derives from sessionStore.userRole via
 * can() (a role promotion re-gates instantly - the old 1 Hz refreshTick
 * re-gate is gone), active/count states derive from roomStore, and clicks
 * go through the handler registry or the imperative panel toggles.
 *
 * Layout contracts from the imperative bar, preserved:
 * - desktop: horizontal wrapping bar in bottom-center, order PINNED_BOTTOM
 *   (hugs the viewport edge; transient banners stack above)
 * - touch: a Shield toggle in the bottom-right FAB stack expanding into a
 *   vertical icon-only column (the lower-left thumbstick spawn zone must
 *   stay clear); first outside tap dismisses; compact geometry rides an
 *   injected !important stylesheet exactly like the original
 * - e2e contract: #qa-btn (visible for presenters, label 'Q&A (n)') and
 *   #breakout-btn; the other ids are kept for continuity
 */
import { useEffect, useState } from 'react';
import { useStore } from 'zustand';
import {
  Shield,
  MapPin,
  Footprints,
  LayoutGrid,
  Vote,
  Hand,
  DoorClosed,
  DoorOpen,
  Lock,
  LockOpen,
} from 'lucide';
import { can } from '../../config/roles.js';
import { TOKENS } from '../designSystem.js';
import { HUD_ORDER } from '../hud/hudRegions.js';
import { isTouchDevice } from '../../input/controlScheme.js';
import { sessionStore } from '../../state/sessionStore';
import { roomStore } from '../../state/roomStore';
import { showNotice } from '../../state/hudStore';
import { toggleBreakoutPanel } from '../breakoutPanel.js';
import { togglePollPanel } from '../pollPanel.js';
import { toggleQaPanel } from '../qaPanel.js';
import { getHandlers } from './handlerRegistry';
import { Icon } from './Icon';
import type { LucideIconNode } from '../../utils/iconCanvas';

const IDLE_BG = 'rgba(0, 0, 0, 0.6)';
const STYLE_ID = 'host-bar-styles';

// UA-based, cannot change mid-session - safe to resolve once (hostBar.js note).
const compact = isTouchDevice();

/**
 * Compact-mode geometry stays a stylesheet: font-size:0 collapses the label
 * visually while leaving the text node in the accessibility tree, so each
 * button keeps its live accessible name ('Q&A (2)' etc).
 */
function injectCompactStyles(): void {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent =
    `#host-bar[data-mode='compact'] .host-action{` +
    `width:44px;height:44px;` +
    `border-radius:50% !important;padding:0 !important;` +
    `gap:0 !important;font-size:0 !important;}`;
  document.head.appendChild(style);
}

interface ActionSpec {
  id: string;
  icon: LucideIconNode;
  label: string;
  title: string;
  activeBg?: string;
  onClick(): void | Promise<void>;
}

function HostActionButton({ action, onActivate }: { action: ActionSpec; onActivate(): void }) {
  const [pending, setPending] = useState(false);
  return (
    <button
      id={action.id}
      type="button"
      className="host-action"
      title={action.title}
      disabled={pending}
      onClick={() => {
        // Collapse the compact column synchronously, before awaiting - every
        // action opens a panel or raises a toast (hostBar.js contract).
        onActivate();
        const result = action.onClick();
        if (result && typeof result.then === 'function') {
          setPending(true);
          result.catch(() => {}).then(() => setPending(false));
        }
      }}
      style={{
        background: action.activeBg ?? IDLE_BG,
        color: TOKENS.text.onDark,
        border: '1px solid rgba(255, 255, 255, 0.2)',
        borderRadius: TOKENS.radius.md,
        padding: '8px 14px',
        minHeight: '36px',
        fontFamily: TOKENS.headingFont,
        fontSize: '13px',
        cursor: 'pointer',
        pointerEvents: 'auto',
        backdropFilter: 'blur(4px)',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px',
        flex: 'none',
        whiteSpace: 'nowrap',
      }}
    >
      <Icon icon={action.icon} size={14} label={action.title} />
      {action.label}
    </button>
  );
}

export function HostBar() {
  const userRole = useStore(sessionStore, (s) => s.userRole);
  const breakout = useStore(roomStore, (s) => s.breakout);
  const poll = useStore(roomStore, (s) => s.poll);
  const qa = useStore(roomStore, (s) => s.qa);
  const locked = useStore(roomStore, (s) => s.roomLocked);
  const lobbyEnabled = useStore(roomStore, (s) => s.lobby.enabled);
  const followActive = useStore(roomStore, (s) => s.followMeActive);
  const [expanded, setExpanded] = useState(false);

  const handsWaiting = qa?.queue.length ?? 0;
  const breakoutActive = breakout?.active === true;
  const pollActive = poll?.active === true;

  const actions: ActionSpec[] = [];
  if (can(userRole, 'presenterMode')) {
    actions.push({
      id: 'gather-btn',
      icon: MapPin as unknown as LucideIconNode,
      label: 'Bring here',
      title: 'Bring everyone to where you are standing',
      onClick: async () => {
        const presenter = getHandlers().presenter;
        if (!presenter) return;
        try {
          await presenter.onGather();
          showNotice('Brought everyone to you');
        } catch (err) {
          showNotice(`Could not bring everyone: ${(err as Error).message}`, true);
        }
      },
    });
    actions.push({
      id: 'follow-me-btn',
      icon: Footprints as unknown as LucideIconNode,
      label: followActive ? 'Guiding' : 'Guide everyone',
      title: followActive
        ? 'Everyone is following you. Click to release them.'
        : 'Lead a tour - everyone follows you until they move',
      activeBg: followActive ? 'rgba(0,150,90,0.85)' : undefined,
      onClick: async () => {
        const presenter = getHandlers().presenter;
        if (!presenter) return;
        try {
          const next = await presenter.onToggleFollow(!followActive);
          showNotice(next ? 'Guiding - everyone follows you' : 'Tour ended - everyone released');
        } catch (err) {
          showNotice(`Follow-me failed: ${(err as Error).message}`, true);
        }
      },
    });
  }
  if (can(userRole, 'manageBreakouts')) {
    actions.push({
      id: 'breakout-btn',
      icon: LayoutGrid as unknown as LucideIconNode,
      label: 'Breakouts',
      title: breakoutActive ? 'Breakout rooms are open' : 'Breakout rooms',
      activeBg: breakoutActive ? 'rgba(25, 118, 210, 0.75)' : undefined,
      onClick: () => toggleBreakoutPanel(),
    });
  }
  if (can(userRole, 'runPolls')) {
    actions.push({
      id: 'poll-btn',
      icon: Vote as unknown as LucideIconNode,
      label: 'Poll',
      title: pollActive ? 'A walk-in poll is running' : 'Walk-in poll',
      activeBg: pollActive ? 'rgba(25, 118, 210, 0.75)' : undefined,
      onClick: () => togglePollPanel(),
    });
  }
  if (can(userRole, 'presenterMode')) {
    actions.push({
      id: 'qa-btn',
      icon: Hand as unknown as LucideIconNode,
      label: handsWaiting ? `Q&A (${handsWaiting})` : 'Q&A',
      title: handsWaiting
        ? `${handsWaiting} ${handsWaiting === 1 ? 'hand is' : 'hands are'} up`
        : 'Q&A queue',
      activeBg: handsWaiting ? 'rgba(245, 158, 11, 0.85)' : undefined,
      onClick: () => toggleQaPanel(),
    });
  }
  if (can(userRole, 'moderate')) {
    actions.push({
      id: 'waiting-room-btn',
      icon: (lobbyEnabled ? DoorClosed : DoorOpen) as unknown as LucideIconNode,
      label: lobbyEnabled ? 'Lobby' : 'Lobby off',
      title: lobbyEnabled
        ? 'Waiting room is on - guests wait for your approval. Click to turn off.'
        : 'Waiting room is off - guests enter directly. Click to turn on.',
      activeBg: lobbyEnabled ? 'rgba(153,0,230,0.6)' : undefined,
      onClick: async () => {
        const lobby = getHandlers().lobby;
        if (!lobby) return;
        try {
          const next = await lobby.onToggleWaitingRoom(!lobbyEnabled);
          showNotice(
            next
              ? 'Waiting room on - guests wait for approval'
              : 'Waiting room off - everyone waiting was let in',
          );
        } catch (err) {
          showNotice(`Waiting room toggle failed: ${(err as Error).message}`, true);
        }
      },
    });
    actions.push({
      id: 'lock-room-btn',
      icon: (locked ? Lock : LockOpen) as unknown as LucideIconNode,
      label: locked ? 'Locked' : 'Lock room',
      title: locked
        ? 'Room is locked - new invite joins are blocked. Click to unlock.'
        : 'Lock the room (blocks new invite joins)',
      activeBg: locked ? 'rgba(216,49,47,0.85)' : undefined,
      onClick: async () => {
        const moderation = getHandlers().moderation;
        if (!moderation) return;
        try {
          const next = await moderation.onToggleLock(!locked);
          showNotice(next ? 'Room locked - new joins blocked' : 'Room unlocked');
        } catch (err) {
          showNotice(`Lock failed: ${(err as Error).message}`, true);
        }
      },
    });
  }

  const anyActions = actions.length > 0;

  // First outside tap dismisses the compact column (capture phase, so it
  // never also starts a look-drag on the canvas).
  useEffect(() => {
    if (!compact || !expanded) return;
    const onOutsidePointer = (e: PointerEvent) => {
      const rootEl = document.getElementById(compact ? 'host-bar-root' : 'host-bar');
      if (rootEl && rootEl.contains(e.target as Node)) return;
      setExpanded(false);
    };
    document.addEventListener('pointerdown', onOutsidePointer, true);
    return () => document.removeEventListener('pointerdown', onOutsidePointer, true);
  }, [expanded]);

  if (!anyActions) return null;

  const buttons = actions.map((a) => (
    <HostActionButton
      key={a.id}
      action={a}
      onActivate={() => {
        if (compact) setExpanded(false);
      }}
    />
  ));

  if (!compact) {
    return (
      <div
        id="host-bar"
        data-mode="bar"
        style={{
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: `${TOKENS.space.sm}px`,
          pointerEvents: 'none',
        }}
      >
        {buttons}
      </div>
    );
  }

  injectCompactStyles();
  return (
    <div
      id="host-bar-root"
      style={{
        display: 'flex',
        // column-reverse + toggle FIRST: the toggle sits lowest (thumb
        // reachable) and the column grows upward.
        flexDirection: 'column-reverse',
        alignItems: 'flex-end',
        gap: `${TOKENS.space.sm}px`,
        pointerEvents: 'none',
      }}
    >
      <button
        id="host-bar-toggle"
        type="button"
        title={expanded ? 'Hide host controls' : 'Host controls'}
        aria-expanded={expanded}
        aria-controls="host-bar"
        onClick={() => setExpanded(!expanded)}
        style={{
          width: '44px',
          height: '44px',
          borderRadius: '50%',
          background: expanded ? 'rgba(25,118,210,0.9)' : 'rgba(0,0,0,0.6)',
          border: '1px solid rgba(255,255,255,0.2)',
          color: TOKENS.text.onDark,
          boxShadow: TOKENS.shadow.sm,
          backdropFilter: 'blur(4px)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flex: 'none',
          pointerEvents: 'auto',
        }}
      >
        <Icon icon={Shield as unknown as LucideIconNode} size={20} label="Host controls" />
      </button>
      <div
        id="host-bar"
        data-mode="compact"
        style={{
          display: expanded ? 'flex' : 'none',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: `${TOKENS.space.sm}px`,
          maxHeight: 'calc(100vh - 240px)',
          overflowY: 'auto',
          pointerEvents: 'none',
        }}
      >
        {buttons}
      </div>
    </div>
  );
}

/** The region + order the bar mounts with (read by the multiplayerUI adapter). */
export const HOST_BAR_MOUNT = compact
  ? ({ region: 'bottom-right', order: HUD_ORDER.ABOVE_FAB_STACK } as const)
  : ({ region: 'bottom-center', order: HUD_ORDER.PINNED_BOTTOM } as const);
